import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import type { FeedbackDTO, FeedbackType } from '@nexply/api-client';
import {
  Badge,
  Button,
  Input,
  Label,
  SegmentedControl,
  Sheet,
  Textarea,
  cn,
  useToast,
} from '@nexply/ui';
import {
  Bug,
  ImagePlus,
  Lightbulb,
  MessageSquarePlus,
  Send,
  Sparkles,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react';
import { api } from '../lib/api.js';
import { captureViewport } from '../lib/screenshot.js';

const TYPES: { value: FeedbackType; label: string; icon: typeof Bug; hint: string }[] = [
  { value: 'BUG', label: 'Bug', icon: Bug, hint: 'Etwas funktioniert nicht' },
  { value: 'FEATURE', label: 'Feature', icon: Sparkles, hint: 'Das fehlt mir noch' },
  { value: 'IMPROVEMENT', label: 'Verbesserung', icon: TrendingUp, hint: 'Geht besser' },
  { value: 'OTHER', label: 'Sonstiges', icon: Lightbulb, hint: 'Alles andere' },
];

export const FEEDBACK_TYPE_LABEL: Record<FeedbackType, string> = {
  BUG: 'Bug',
  FEATURE: 'Feature',
  IMPROVEMENT: 'Verbesserung',
  OTHER: 'Sonstiges',
};

export const FEEDBACK_STATUS_LABEL: Record<FeedbackDTO['status'], string> = {
  OPEN: 'Offen',
  PLANNED: 'Geplant',
  IN_PROGRESS: 'In Arbeit',
  DONE: 'Erledigt',
  REJECTED: 'Abgelehnt',
};

export const FEEDBACK_STATUS_TONE: Record<
  FeedbackDTO['status'],
  'neutral' | 'gold' | 'green' | 'red' | 'warn'
> = {
  OPEN: 'neutral',
  PLANNED: 'gold',
  IN_PROGRESS: 'warn',
  DONE: 'green',
  REJECTED: 'red',
};

const MAX_SCREENSHOT_BYTES = 5_000_000;

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shot, setShot] = useState<string | null>(null);
  const [shotFailed, setShotFailed] = useState(false);
  // Zählt jede Aufnahme hoch – dient als React-key, damit das Formular je
  // Öffnen frisch startet (der Screenshot selbst wäre als key riesig).
  const [captureId, setCaptureId] = useState(0);

  // Der Screenshot entsteht VOR dem Öffnen des Fensters – sonst fotografiert
  // sich das Feedback-Fenster selbst statt der Seite, um die es geht.
  async function openWithScreenshot() {
    setBusy(true);
    setCaptureId((n) => n + 1);
    try {
      const dataUrl = await captureViewport();
      setShot(dataUrl);
      setShotFailed(false);
    } catch (e) {
      console.error('[Feedback] Screenshot fehlgeschlagen:', e);
      setShot(null);
      setShotFailed(true);
    } finally {
      setBusy(false);
      setOpen(true);
    }
  }

  return (
    <>
      <button
        data-screenshot-hide
        onClick={() => void openWithScreenshot()}
        disabled={busy}
        title="Feedback geben"
        aria-label="Feedback geben"
        className={cn(
          'fixed z-30 flex items-center gap-2 rounded-full border border-gold/25 bg-surface-3/90 backdrop-blur',
          'text-gold-light shadow-card hover:border-gold/50 hover:bg-surface-3 active:scale-95 transition',
          'disabled:opacity-60 disabled:active:scale-100',
          'right-4 bottom-[calc(env(safe-area-inset-bottom)+96px)] h-12 w-12 justify-center',
          'lg:right-6 lg:bottom-6 lg:h-11 lg:w-auto lg:px-4',
        )}
      >
        {busy ? (
          <span className="inline-block h-4 w-4 shrink-0 rounded-full border-2 border-current border-r-transparent animate-spin" />
        ) : (
          <MessageSquarePlus className="h-5 w-5 shrink-0" />
        )}
        <span className="hidden lg:inline text-sm font-medium">
          {busy ? 'Moment…' : 'Feedback'}
        </span>
      </button>

      <FeedbackSheet
        open={open}
        onClose={() => setOpen(false)}
        shot={shot}
        shotFailed={shotFailed}
        captureId={captureId}
      />
    </>
  );
}

function FeedbackSheet({
  open,
  onClose,
  shot,
  shotFailed,
  captureId,
}: {
  open: boolean;
  onClose: () => void;
  shot: string | null;
  shotFailed: boolean;
  captureId: number;
}) {
  const [tab, setTab] = useState<'new' | 'mine'>('new');

  useEffect(() => {
    if (open) setTab('new');
  }, [open]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Feedback & Vorschläge"
      description="Hilf uns, Nexply besser zu machen – melde Bugs oder schlage Features vor."
      size="lg"
    >
      <div className="space-y-5">
        <SegmentedControl
          size="sm"
          value={tab}
          onChange={(v) => setTab(v)}
          options={[
            { value: 'new', label: 'Neu' },
            { value: 'mine', label: 'Meine Vorschläge' },
          ]}
        />
        {tab === 'new' ? (
          // Frisches Formular je Öffnen – inklusive des gerade aufgenommenen Bildes.
          <NewFeedbackForm
            key={captureId}
            initialShot={shot}
            shotFailed={shotFailed}
            onDone={onClose}
          />
        ) : (
          <MyFeedbackList />
        )}
      </div>
    </Sheet>
  );
}

function NewFeedbackForm({
  onDone,
  initialShot,
  shotFailed,
}: {
  onDone: () => void;
  initialShot: string | null;
  shotFailed: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const fileRef = useRef<HTMLInputElement>(null);

  const [type, setType] = useState<FeedbackType>('FEATURE');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [shot, setShot] = useState<string | null>(initialShot);
  const [attach, setAttach] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api.feedback.create({
        type,
        title: title.trim(),
        body: body.trim() || undefined,
        pageUrl: path,
        screenshotDataUrl: attach && shot ? shot : undefined,
      }),
    onSuccess: () => {
      toast.success('Danke! Dein Feedback ist angekommen.');
      setTitle('');
      setBody('');
      setShot(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['feedback', 'mine'] });
      onDone();
    },
    onError: (e: { message?: string }) => setError(e.message ?? 'Senden fehlgeschlagen.'),
  });

  async function readImage(file: File | null | undefined) {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      setError('Nur PNG, JPEG oder WebP möglich.');
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setError('Bild zu groß (max. 5 MB).');
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
    setShot(dataUrl);
    setError(null);
  }

  const canSubmit = title.trim().length > 0 && !submit.isPending;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) submit.mutate();
      }}
      onPaste={(e) => {
        const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
          i.type.startsWith('image/'),
        );
        if (item) void readImage(item.getAsFile());
      }}
    >
      <div>
        <Label>Typ</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const active = t.value === type;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={cn(
                  'flex items-start gap-2.5 rounded-xl border p-3 text-left transition active:scale-[0.98]',
                  active
                    ? 'border-gold/40 bg-gold/10 text-text'
                    : 'border-white/[0.06] bg-surface-2 text-text-dim hover:border-white/[0.12]',
                )}
              >
                <Icon
                  className={cn('h-4 w-4 mt-0.5 shrink-0', active ? 'text-gold' : 'text-text-muted')}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{t.label}</span>
                  <span className="block text-[11px] text-text-muted truncate">{t.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label htmlFor="fb-title">Titel</Label>
        <Input
          id="fb-title"
          value={title}
          maxLength={200}
          placeholder="Kurz und knackig: worum geht es?"
          onChange={(e) => setTitle(e.currentTarget.value)}
          required
        />
      </div>

      <div>
        <Label htmlFor="fb-body">Beschreibung</Label>
        <Textarea
          id="fb-body"
          rows={5}
          maxLength={5000}
          value={body}
          placeholder="Beschreibe es so genau wie möglich. Bei Bugs gerne die Schritte zum Nachstellen."
          onChange={(e) => setBody(e.currentTarget.value)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between gap-3">
          <Label className="!mb-0">Screenshot</Label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="text-xs text-gold-light hover:text-gold transition inline-flex items-center gap-1.5"
            >
              <ImagePlus className="h-3.5 w-3.5" />
              {shot ? 'Ersetzen' : 'Bild wählen'}
            </button>
            <label className="inline-flex items-center gap-1.5 text-xs text-text-dim cursor-pointer select-none">
              <input
                type="checkbox"
                checked={attach}
                onChange={(e) => setAttach(e.currentTarget.checked)}
                className="h-3.5 w-3.5 accent-gold"
              />
              Anhängen
            </label>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => void readImage(e.currentTarget.files?.[0])}
        />
        {shot ? (
          <>
            <div
              className={cn(
                'mt-2 relative overflow-hidden rounded-xl border border-white/[0.08] transition',
                !attach && 'opacity-40 grayscale',
              )}
            >
              <img
                src={shot}
                alt="Screenshot der aktuellen Seite"
                className="max-h-48 w-full object-contain bg-black/40"
              />
              <button
                type="button"
                onClick={() => setShot(null)}
                className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-text-dim hover:text-danger transition"
                aria-label="Screenshot entfernen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-text-muted">
              Automatisch beim Öffnen aufgenommen.
            </p>
          </>
        ) : shotFailed ? (
          <p className="mt-2 text-[11px] text-warn">
            Automatischer Screenshot hat nicht geklappt – du kannst ein Bild auswählen oder hier
            einfügen (Strg+V).
          </p>
        ) : (
          <p className="mt-2 text-[11px] text-text-muted">
            Bild auswählen oder einfach hier einfügen (Strg+V).
          </p>
        )}
      </div>

      <div className="rounded-xl border border-white/[0.05] bg-white/[0.02] px-3 py-2 text-[11px] text-text-muted">
        Wird mitgeschickt: aktuelle Seite <span className="text-text-dim">{path}</span>
      </div>

      {error && <div className="text-xs text-danger">{error}</div>}

      <Button type="submit" disabled={!canSubmit} className="w-full">
        <Send className="h-4 w-4" />
        {submit.isPending ? 'Wird gesendet…' : 'Absenden'}
      </Button>
    </form>
  );
}

function MyFeedbackList() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['feedback', 'mine'],
    queryFn: () => api.feedback.mine(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.feedback.remove(id),
    onSuccess: () => {
      toast.success('Zurückgezogen.');
      void qc.invalidateQueries({ queryKey: ['feedback', 'mine'] });
    },
    onError: (e: { code?: string }) =>
      toast.error(
        e.code === 'already_in_progress'
          ? 'Wird bereits bearbeitet – kann nicht mehr zurückgezogen werden.'
          : 'Löschen fehlgeschlagen.',
      ),
  });

  if (isLoading) return <div className="text-sm text-text-muted py-6 text-center">Lädt…</div>;
  if (!data || data.feedback.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-white/[0.08] py-10 text-center">
        <div className="text-sm text-text-dim">Noch kein Feedback abgeschickt.</div>
        <div className="mt-1 text-xs text-text-muted">
          Deine Vorschläge erscheinen hier mit ihrem Status.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {data.feedback.map((f) => (
        <div key={f.id} className="rounded-xl border border-white/[0.06] bg-surface-2 p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-text truncate">{f.title}</div>
              <div className="mt-0.5 text-[11px] text-text-muted">
                {FEEDBACK_TYPE_LABEL[f.type]} · {new Date(f.createdAt).toLocaleDateString('de-DE')}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge tone={FEEDBACK_STATUS_TONE[f.status]}>{FEEDBACK_STATUS_LABEL[f.status]}</Badge>
              {f.status === 'OPEN' && (
                <button
                  onClick={() => remove.mutate(f.id)}
                  disabled={remove.isPending}
                  className="grid h-7 w-7 place-items-center rounded-lg text-text-muted hover:text-danger hover:bg-danger/10 transition"
                  aria-label="Zurückziehen"
                  title="Zurückziehen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          {f.body && (
            <p className="mt-2 text-xs text-text-dim whitespace-pre-wrap">{f.body}</p>
          )}
          {f.adminNote && (
            <div className="mt-2.5 rounded-lg border border-gold/20 bg-gold/[0.06] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gold/80">Antwort vom Team</div>
              <div className="mt-0.5 text-xs text-text-dim whitespace-pre-wrap">{f.adminNote}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
